import { debounce } from 'lodash';
import { useCallback, useState, useEffect, useMemo, useRef } from 'react';
import Spinner from '../Spinner';
import Modal from 'common/components/Modal';
import BEM from 'common/bem';
import Button from 'admin/shared-components/Button';
import StyledInput from 'admin/shared-components/StyledInput';
import SearchIcon from 'admin/icons/search';
import { Checkbox } from 'admin/shared-components/Checkbox/Checkbox';
import { type LocationT, type PartialPersona } from 'js-sdk/src/types/Locations';
import { useFindPersonasQuery } from 'redux-stores/persona/personasApi';
import { useUpdateLocationPersonasMutation } from 'redux-stores/admin/locationsApi';

const classes = BEM.with('ManageLocationsModal');

export type ManageLocationsModalProps = {
  closeModal: () => void;
  isOpen: boolean;
  options: {
    location: LocationT;
    organizationId: string;
    resetData: () => void;
    onSuccessToast: (message: string) => void;
    onErrorToast: (message: string) => void;
  };
};

type PersonasListProps = {
  selectedPersonas: PartialPersona[];
  personas: PartialPersona[];
  title?: string;
  allChecked?: boolean;
  emptyMessage?: string;
  onPersonaSelect: (persona: PartialPersona) => void;
  onPersonaUnselect: (persona: PartialPersona) => void;
};

const PersonasList = ({
  selectedPersonas,
  personas,
  title,
  allChecked,
  emptyMessage,
  onPersonaSelect,
  onPersonaUnselect,
}: PersonasListProps) => {
  return (
    <>
      {title ? <h3>{title}</h3> : null}
      {personas?.length || selectedPersonas?.length ? (
        <ul className={classes('list')}>
          {selectedPersonas?.length
            ? selectedPersonas.map((persona) => (
                <li key={`selected-persona-${persona.id}`}>
                  <Checkbox
                    onClick={() => onPersonaUnselect(persona)}
                    checked={true}
                    label={persona.name}
                    isLabelClickable={true}
                  />
                </li>
              ))
            : null}
          {personas?.length
            ? personas.map((persona) => (
                <li key={persona.id}>
                  <Checkbox
                    checked={allChecked ? true : false}
                    onClick={() => onPersonaSelect(persona)}
                    label={persona.name}
                    isLabelClickable={true}
                  />
                </li>
              ))
            : null}
        </ul>
      ) : (
        <div className={classes('empty-message')}>{emptyMessage}</div>
      )}
    </>
  );
};

const ModalContent = ({ closeModal, options, isOpen }: ManageLocationsModalProps) => {
  const [tooltipOpen, setTooltipOpen] = useState<string | null>(null);
  const [personasState, setPersonasState] = useState<{
    personas: PartialPersona[];
    query: string;
    personasToAdd: PartialPersona[];
    personasToRemove: PartialPersona[];
    selectedPersonas: PartialPersona[];
    inputValue: string;
  }>({
    personas: [],
    query: '',
    personasToAdd: [],
    personasToRemove: [],
    selectedPersonas: options.location.personas || [],
    inputValue: '',
  });

  const {
    data: personas,
    isLoading,
    isFetching,
  } = useFindPersonasQuery(
    {
      orgId: options.organizationId,
      searchValue: personasState.query,
      startIndex: 1,
      count: 100,
    },
    { refetchOnMountOrArgChange: true }
  );

  const [updateLocationPersonas, { isLoading: isUpdatingLocationPersonas }] =
    useUpdateLocationPersonasMutation();

  const tooltipRef = useRef<HTMLDivElement>(null);

  const searchPersonas = useCallback(async (personaName: string) => {
    setPersonasState((currentState) => ({
      ...currentState,
      query: personaName,
    }));
  }, []);

  const searchPersonasDebounced = debounce(searchPersonas, 300);

  useEffect(() => {
    searchPersonasDebounced(personasState.inputValue);
  }, [searchPersonasDebounced, personasState.inputValue]);

  useEffect(() => {
    setPersonasState((currentState) => ({
      ...currentState,
      personas:
        personas?.data.map((persona) => ({
          id: persona.personaId,
          name: persona.name,
        })) || [],
    }));
  }, [personas?.data]);

  useEffect(() => {
    searchPersonasDebounced(personasState.inputValue);
  }, [searchPersonasDebounced, personasState.inputValue]);

  const onPersonaSelect = useCallback((persona: PartialPersona) => {
    setPersonasState((currentState) => {
      const index = currentState.personas.findIndex((p) => p.id === persona.id);
      const personasToAdd = currentState.personas[index].id;
      if (currentState.personasToRemove.some((p) => p.id === personasToAdd)) {
        return {
          ...currentState,
          personasToRemove: currentState.personasToRemove.filter((p) => p.id !== personasToAdd),
          selectedPersonas: [...currentState.selectedPersonas, persona],
        };
      }
      return {
        ...currentState,
        personasToAdd: [...currentState.personasToAdd, persona],
        selectedPersonas: [...currentState.selectedPersonas, persona],
      };
    });
  }, []);

  const onPersonaUnselect = useCallback((persona: PartialPersona) => {
    setPersonasState((currentState) => {
      const index = currentState.selectedPersonas.findIndex((p) => p.id === persona.id);
      const selectedPersonas = [...currentState.selectedPersonas];
      selectedPersonas.splice(index, 1);
      if (currentState.personasToAdd.some((p) => p.id === persona.id)) {
        return {
          ...currentState,
          personasToAdd: currentState.personasToAdd.filter((p) => p.id !== persona.id),
          selectedPersonas: [...selectedPersonas],
        };
      }
      return {
        ...currentState,
        personasToRemove: [...currentState.personasToRemove, persona],
        selectedPersonas: [...selectedPersonas],
      };
    });
  }, []);

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (
        tooltipRef.current &&
        e.target instanceof Node &&
        !tooltipRef.current.contains(e.target)
      ) {
        const container = document.getElementById('manage-personas-modal');
        container?.scrollTo({ top: container.scrollHeight, behavior: 'smooth' });
        setTooltipOpen(null);
      }
    }
    if (tooltipOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [tooltipOpen]);

  const availablePersonas = useMemo(() => {
    return personasState.personas.filter(
      (persona) => !personasState.selectedPersonas.some((p) => p.id === persona.id)
    );
  }, [personasState.personas, personasState.selectedPersonas]);

  const onSave = useCallback(async () => {
    try {
      await updateLocationPersonas({
        orgId: options.organizationId,
        locationId: options.location.id,
        locationType: options.location.type,
        associationsToAdd: personasState.personasToAdd.map((p) => p.id) || [],
        associationsToRemove: personasState.personasToRemove.map((p) => p.id) || [],
      }).unwrap();
      options.resetData();
      closeModal();
      options.onSuccessToast('Personas updated successfully.');
    } catch (error) {
      closeModal();
      options.resetData();
      console.error('Error updating location personas', error);
      options.onErrorToast(`Error updating Location Personas.`);
    }
  }, [
    updateLocationPersonas,
    options,
    personasState.personasToAdd,
    personasState.personasToRemove,
    closeModal,
  ]);

  const saveIsDisabled =
    (!personasState.personasToAdd.length && !personasState.personasToRemove.length) ||
    isUpdatingLocationPersonas;

  return (
    <Modal
      id="manage-personas-modal"
      bodyClass={classes('body')}
      closeClass={classes('close-button')}
      footerClass={classes('footer')}
      header={
        <div className={classes('title')}>
          <h2>Manage Personas</h2>
          <p>{options.location.name}</p>
        </div>
      }
      headerClass={classes('header')}
      headerTextStyle={classes('header-text')}
      isOpen={isOpen}
      size={'medium'}
      footerPrimaryActions={
        <Button variant="primary" size="medium" onClick={onSave} disabled={saveIsDisabled}>
          Save
        </Button>
      }
      footerSecondaryActions={
        <>
          {tooltipOpen && (
            <div ref={tooltipRef} className={`tooltip`}>
              <p>You have unsaved changes</p>
              <div className="tooltip-actions">
                <Button variant="secondary" size="medium" onClick={() => closeModal()}>
                  Discard
                </Button>
                <Button variant="primary" onClick={() => setTooltipOpen(null)}>
                  Keep Editing
                </Button>
              </div>
            </div>
          )}
          <Button
            disabled={isUpdatingLocationPersonas}
            variant="secondary"
            size="medium"
            onClick={() =>
              personasState.personasToAdd.length || personasState.personasToRemove.length
                ? setTooltipOpen('close')
                : closeModal()
            }
          >
            Cancel
          </Button>
        </>
      }
      onRequestClose={() =>
        personasState.personasToAdd.length || personasState.personasToRemove.length
          ? setTooltipOpen('close')
          : closeModal()
      }
      borderStyle="rounded"
      className={classes()}
    >
      <div className={classes('add-form')}>
        <StyledInput
          id="find-persona"
          label="Find Persona"
          value={personasState.inputValue}
          onChange={(e) =>
            setPersonasState((currentState) => ({
              ...currentState,
              inputValue: e.target.value,
            }))
          }
          startIcon={<SearchIcon />}
          variant="outlined"
          fullWidth
        />
        {isLoading || isFetching || isUpdatingLocationPersonas ? (
          <Spinner />
        ) : (
          <PersonasList
            onPersonaSelect={(persona) => onPersonaSelect(persona)}
            onPersonaUnselect={(persona) => onPersonaUnselect(persona)}
            selectedPersonas={personasState.query.length ? [] : personasState.selectedPersonas}
            personas={availablePersonas}
            emptyMessage="No personas found"
          />
        )}
      </div>
    </Modal>
  );
};

export default ModalContent;
